import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Alert, Box, Button, Stack, Text } from '@chakra-ui/react';
import SimpleModal from '../components/SimpleModal';

type NotificationType = 'success' | 'error' | 'info';

interface Notification {
    message: string;
    type: NotificationType;
}

interface NotificationContextType {
    notify: (notification: Notification) => void;
}

// Confirmation Context Types
interface ConfirmationOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmButtonClass?: string;
    confirmColorScheme?: string;
    onConfirm: () => void;
    onCancel?: () => void;
}

interface ConfirmationContextType {
    confirm: (options: ConfirmationOptions) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const useConfirmation = () => {
    const context = useContext(ConfirmationContext);
    if (!context) {
        throw new Error('useConfirmation must be used within a ConfirmationProvider');
    }
    return context;
};

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [confirmationState, setConfirmationState] = useState<ConfirmationOptions | null>(null);

    const confirm = (options: ConfirmationOptions) => {
        setConfirmationState(options);
    };

    const handleConfirm = () => {
        if (confirmationState) {
            confirmationState.onConfirm();
            setConfirmationState(null);
        }
    };

    const handleCancel = () => {
        if (confirmationState?.onCancel) {
            confirmationState.onCancel();
        }
        setConfirmationState(null);
    };

    const resolveColorPalette = () => {
        if (confirmationState?.confirmColorScheme) return confirmationState.confirmColorScheme;
        const cls = confirmationState?.confirmButtonClass || '';
        if (cls.includes('danger')) return 'red';
        if (cls.includes('warning')) return 'yellow';
        if (cls.includes('success')) return 'green';
        return 'brand';
    };

    return (
        <ConfirmationContext.Provider value={{ confirm }}>
            {children}
            {confirmationState && (
                <SimpleModal
                    isOpen={!!confirmationState}
                    onClose={handleCancel}
                    title={confirmationState.title || 'Confirm Action'}
                    size="sm"
                    showCloseButton={false}
                    footer={
                        <Stack direction="row" justify="flex-end" gap={3}>
                            <Button onClick={handleCancel} variant="ghost">
                                {confirmationState.cancelText || 'Cancel'}
                            </Button>
                            <Button colorPalette={resolveColorPalette()} onClick={handleConfirm}>
                                {confirmationState.confirmText || 'Confirm'}
                            </Button>
                        </Stack>
                    }
                >
                    <Text>{confirmationState.message}</Text>
                </SimpleModal>
            )}
        </ConfirmationContext.Provider>
    );
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<Array<Notification & { id: number }>>([]);

    const notify = useCallback((notification: Notification) => {
        const id = Date.now();
        setMessages(prev => [...prev, { ...notification, id }]);
        setTimeout(() => {
            setMessages(prev => prev.filter(msg => msg.id !== id));
        }, 4000);
    }, []);

    // Listen for 401 unauthorized events from the API
    useEffect(() => {
        const handleUnauthorized = (event: CustomEvent<{ message: string }>) => {
            notify({ type: 'error', message: event.detail.message });
        };

        window.addEventListener('api-unauthorized', handleUnauthorized as EventListener);
        return () => {
            window.removeEventListener('api-unauthorized', handleUnauthorized as EventListener);
        };
    }, [notify]);

    return (
        <NotificationContext.Provider value={{ notify }}>
            {children}
            <Box position="fixed" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={1600} display="flex" flexDirection="column" gap={3}>
                {messages.map(msg => (
                    <Alert.Root key={msg.id} status={msg.type} borderRadius="md" boxShadow="lg">
                        <Alert.Content>
                            <Alert.Description>{msg.message}</Alert.Description>
                        </Alert.Content>
                    </Alert.Root>
                ))}
            </Box>
        </NotificationContext.Provider>
    );
};



